import { useRef, useEffect, useCallback } from "react";
import { makeStyles } from "@fluentui/react-components";

import { tokens } from "../../theme";

import {
    useAppDispatch,
    useAppSelector,
    selectAnswers,
    selectStreamedAnswers,
    selectLastQuestion,
    selectChatLoading,
    selectIsStreaming,
    selectChatError,
    sendChatMessage,
    submitChatFeedback,
    submitChatAdditionalFeedback,
    clearChatState,
    selectChatFeedbackEnabled,
    selectStreamingEnabled
} from "../../store";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { useChatContext } from "../layout/Layout";
import { brand } from "@brand";
// Domain selection now managed by Layout and passed via context

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalNone: tokens.primitives["spacing-Vertical-None"],
    spacingVerticalXS: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingVerticalXL: tokens.primitives["spacing-Vertical-XL"],
    spacingVerticalXXL: tokens.primitives["spacing-Vertical-XXL"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],
    spacingHorizontalXL: tokens.primitives["spacing-Horizontal-XL"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    lineHeightBase300: tokens.primitives["lineHeight-Base-300"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    neutralPrimaryForeground: tokens.semantic.neutralPrimaryForeground,
    neutralPrimaryBackground: tokens.semantic.neutralPrimaryBackground,
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],

    // Radius
    radiusMedium: tokens.primitives["stroke-Radius-Medium"],
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],

    // Shadows
    shadowRestingSmall: tokens.shadows["shadow-Resting-Small"],

    // Stroke
    strokeWidthThin: tokens.primitives["stroke-Width-Thin"],
    strokeDefault: tokens.primitives.strokeDefault,
    strokeFocus: tokens.primitives.strokeFocus,

    // Component tokens
    chatMaxWidth: tokens.components.chatMaxWidth,
    chatGptMaxWidth: tokens.components.chatGptMaxWidth
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    // Layout
    container: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden"
    },

    chatRoot: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
        height: "100%",
        overflow: "hidden"
    },

    chatContainer: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
        height: "100%",
        overflow: "hidden"
    },

    // Empty state
    chatEmptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        padding: t.spacingVerticalXXL,
        textAlign: "center"
    },

    chatEmptyStateExampleQs: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalM,
        width: "100%",
        maxWidth: "900px"
    },

    // Message stream - scrollbar on right edge
    chatMessageStreamContainer: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%"
    },

    // Inner content wrapper - centered
    chatMessageStreamInner: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: t.chatMaxWidth,
        marginLeft: "auto",
        marginRight: "auto",
        padding: `${t.spacingVerticalL} ${t.spacingHorizontalL}`
    },

    chatMessageStream: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalL,
        width: "100%"
    },

    // Message containers
    chatMessageGpt: {
        display: "flex",
        flexDirection: "column",
        maxWidth: t.chatGptMaxWidth,
        width: "100%",

        "@media (max-width: 768px)": {
            maxWidth: "none"
        }
    },

    chatMessageGptMinWidth: {
        display: "flex",
        flexDirection: "column",
        maxWidth: t.chatGptMaxWidth,
        minWidth: "300px",
        width: "100%"
    },

    // Footer / Input area
    footerContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        padding: `${t.spacingVerticalM} ${t.spacingHorizontalL}`,
        backgroundColor: t.neutralWhite,
        borderTop: `${t.strokeWidthThin} solid ${t.strokeDefault}`,
        flexShrink: 0,
    },

    chatInputContainer: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: t.chatMaxWidth,
        marginLeft: "auto",
        marginRight: "auto"
    },

    chatInputPlaceholder: {
        height: t.spacingVerticalNone
    },

    // Add this new style
    chatMessagePair: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalL // Spacing between user message and answer
    },

    // Drawer styles
    drawerContent: {
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalM
    },

    settingItem: {
        marginBottom: t.spacingVerticalM,
        display: "flex",
        flexDirection: "column",
        gap: t.spacingVerticalXS,
        "& label": {
            fontSize: t.fontSizeBase300,
            fontWeight: t.fontWeightMedium as unknown as number,
            color: t.neutralPrimaryForeground
        }
    },

    checkbox: {
        marginBottom: t.spacingVerticalM
    },

    drawer: {
        "& .fui-DrawerHeader": {
            borderBottom: `${t.strokeWidthThin} solid ${t.strokeDefault}`
        },
        "& .fui-DrawerFooter": {
            borderTop: `${t.strokeWidthThin} solid ${t.strokeDefault}`
        }
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

const Chat = () => {
    const styles = useStyles();
    const dispatch = useAppDispatch();

    // Redux selectors
    const answers = useAppSelector(selectAnswers);
    const streamedAnswers = useAppSelector(selectStreamedAnswers);
    const lastQuestion = useAppSelector(selectLastQuestion);
    const isLoading = useAppSelector(selectChatLoading);
    const isStreaming = useAppSelector(selectIsStreaming);
    const error = useAppSelector(selectChatError);
    const chatFeedbackEnabled = useAppSelector(selectChatFeedbackEnabled);
    const streamingEnabled = useAppSelector(selectStreamingEnabled);

    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const { setChatStarted, setClearChatCallback, effectiveDomainSlug, domainConfig, setSelectedSlug } = useChatContext();

    // Silent base selection: when the backend auto-resolves a base from
    // the user's message (because no client-side selection was active),
    // the response carries `selected_domain` on the FIRST stream chunk.
    // Apply it as soon as it arrives so the dropdown flips within
    // ~500ms of hitting send rather than waiting for the full answer to
    // finish streaming. Watches BOTH streamedAnswers (during stream) and
    // answers (post-stream fallback for non-streaming responses).
    useEffect(() => {
        const latestStreaming = streamedAnswers[streamedAnswers.length - 1];
        const latestFinal = answers[answers.length - 1];
        const resolved =
            latestStreaming?.[1]?.selected_domain ??
            latestFinal?.[1]?.selected_domain;
        if (resolved?.slug && resolved.slug !== effectiveDomainSlug) {
            setSelectedSlug(resolved.slug);
        }
    }, [streamedAnswers, answers, effectiveDomainSlug, setSelectedSlug]);

    // Hard mode: block chat input until a domain is selected
    const hardModeBlocked = domainConfig?.mode === "hard" && !effectiveDomainSlug;

    const clearChat = useCallback(() => {
        dispatch(clearChatState());
        setChatStarted(false);
    }, [dispatch, setChatStarted]);

    useEffect(() => {
        setClearChatCallback(clearChat);
    }, [clearChat, setClearChatCallback]);

    // Scroll to bottom using the container's scrollTop instead of scrollIntoView.
    // scrollIntoView on mobile can scroll the entire viewport (not just the
    // overflow container), causing the input area to jump during streaming.
    const scrollToBottom = useCallback((smooth: boolean) => {
        const el = scrollContainerRef.current;
        if (el) {
            el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
        }
    }, []);

    useEffect(() => scrollToBottom(true), [isLoading, scrollToBottom]);
    useEffect(() => scrollToBottom(false), [streamedAnswers, scrollToBottom]);

    const makeApiRequest = useCallback(
        (question: string) => {
            setChatStarted(true);
            void dispatch(sendChatMessage({ question, shouldStream: streamingEnabled, domainSlug: effectiveDomainSlug }));
        },
        [dispatch, setChatStarted, effectiveDomainSlug]
    );

    const onFeedbackSubmitted = useCallback(
        (rating: number, conversationLogId: string) => {
            void dispatch(submitChatFeedback({ rating, conversationLogId }));
        },
        [dispatch]
    );

    const onAdditionalFeedbackSubmitted = useCallback(
        (conversationLogId: string, feedbackOptions: { option_id: number; option_text: string }[], userInput: string) => {
            void dispatch(submitChatAdditionalFeedback({ conversationLogId, feedbackOptions, userInput }));
        },
        [dispatch]
    );

    const onExampleClicked = useCallback(
        (example: string) => {
            makeApiRequest(example);
        },
        [makeApiRequest]
    );

    const onShowCitation = useCallback((citation: string) => {
        window.open(citation, "_blank");
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestion ? (
                        <div className={styles.chatEmptyState}>
                            <div className={styles.chatEmptyStateExampleQs}>
                                <ExampleList onExampleClicked={onExampleClicked} />
                            </div>
                        </div>
                    ) : (
                        <div
                            ref={scrollContainerRef}
                            className={styles.chatMessageStreamContainer}
                            role="log"
                            aria-label="Chat history"
                            aria-live="polite"
                            aria-relevant="additions"
                        >
                            <div className={styles.chatMessageStreamInner}>
                                <div className={styles.chatMessageStream}>
                                    {isStreaming &&
                                        streamedAnswers.map((streamedAnswer, index) => {
                                            // Only the LAST streamedAnswer is currently being
                                            // generated. Prior turns in the same render pass are
                                            // historical and complete — they shouldn't show the
                                            // typing indicator. Pinning isStreaming=true on every
                                            // entry was painting "dancing dots" on every prior
                                            // assistant message during a new turn.
                                            const isLast = index === streamedAnswers.length - 1;
                                            return (
                                                <div key={index} className={styles.chatMessagePair}>
                                                    <UserChatMessage message={streamedAnswer[0]} />
                                                    <div className={styles.chatMessageGpt}>
                                                        <Answer
                                                            isStreaming={isLast}
                                                            key={index}
                                                            answer={streamedAnswer[1]}
                                                            isSelected={false}
                                                            onCitationClicked={c => onShowCitation(c)}
                                                            onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                            showFollowupQuestions={false}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    {!isStreaming &&
                                        answers.map((answer, index) => (
                                            <div key={index} className={styles.chatMessagePair}>
                                                <UserChatMessage message={answer[0]} />
                                                <div className={styles.chatMessageGpt}>
                                                    <Answer
                                                        key={index}
                                                        answer={answer[1]}
                                                        isStreaming={false}
                                                        isSelected={false}
                                                        onCitationClicked={c => onShowCitation(c)}
                                                        onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                        onFeedbackClicked={chatFeedbackEnabled ? (rating => onFeedbackSubmitted(rating, answer[1].conversation_log?.id ?? "")) : undefined}
                                                        onAdditionalFeedbackSubmitted={chatFeedbackEnabled ? ((feedbackOptions, userInput) =>
                                                            onAdditionalFeedbackSubmitted(answer[1].conversation_log?.id ?? "", feedbackOptions, userInput)
                                                        ) : undefined}
                                                        showFollowupQuestions={true}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    {!isStreaming && isLoading && (
                                        <div className={styles.chatMessagePair}>
                                            <UserChatMessage message={lastQuestion} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerLoading />
                                            </div>
                                        </div>
                                    )}
                                    {error ? (
                                        <div className={styles.chatMessagePair}>
                                            <UserChatMessage message={lastQuestion} />
                                            <div className={styles.chatMessageGptMinWidth}>
                                                <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestion)} />
                                            </div>
                                        </div>
                                    ) : null}
                                    <div ref={chatMessageStreamEnd} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.footerContainer}>
                        <div id="chatInputContainer" className={styles.chatInputContainer}>
                            <QuestionInput
                                clearOnSend
                                placeholder={hardModeBlocked ? (domainConfig?.selectionPrompt ?? `Select a ${brand.domainNoun.singular} to start chatting`) : "Type a new question..."}
                                disabled={isLoading || hardModeBlocked}
                                onSend={question => makeApiRequest(question)}
                                onClearChat={clearChat}
                                clearChatDisabled={!lastQuestion || isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.chatInputPlaceholder}></div>
        </div>
    );
};

export default Chat;
